// SCU Incompatibility Warning Component
// Displays warnings when cargo cannot be sold due to SCU box size restrictions

import React, { useMemo } from 'react';
import './ScuIncompatibilityWarning.css';

/**
 * ScuIncompatibilityWarning - Shows warnings for cargo items that can't be sold
 * due to SCU size restrictions at available merchants
 *
 * @param {Array} cargoItems - Array of cargo items with { commodityId, commodityName, scu, quantity }
 * @param {Array} merchantCommodities - Array of merchant commodity data with accepted_scus
 * @param {string} currentSystem - Current system name for filtering suggestions
 */
const ScuIncompatibilityWarning = ({
  cargoItems = [],
  merchantCommodities = [],
  currentSystem = null
}) => {
  // Analyze cargo items for SCU compatibility issues
  const incompatibleItems = useMemo(() => {
    if (!cargoItems.length || !merchantCommodities.length) return [];

    return cargoItems
      .filter(item => item.commodityId && Number(item.quantity) > 0 && !item.actualPrice)
      .map(item => {
        const itemScu = Number(item.scu) || 1;
        const commodityId = item.commodityId;

        // Find all merchants that buy this commodity
        const buyingMerchants = merchantCommodities.filter(
          mc => mc.commodity_id === commodityId &&
                String(mc.activity).toLowerCase() === 'buy' &&
                (mc.last_buy_price > 0 || (mc.price_history && mc.price_history.length > 0))
        );

        if (buyingMerchants.length === 0) {
          return null; // No merchants buy this at all
        }

        // Check which merchants accept this SCU size
        const acceptingMerchants = buyingMerchants.filter(mc => {
          const acceptedScus = mc.accepted_scus || [];
          if (acceptedScus.length === 0) return true; // No restriction data
          return acceptedScus.some(s => Number(s) === itemScu);
        });

        if (acceptingMerchants.length > 0) {
          return null; // At least one merchant accepts this size
        }

        // Get all accepted sizes across all merchants for this commodity
        const allAcceptedSizes = new Set();
        buyingMerchants.forEach(mc => {
          (mc.accepted_scus || []).forEach(s => allAcceptedSizes.add(Number(s)));
        });

        // Find merchants that accept larger sizes (might accept this commodity in bigger boxes)
        const largerSizeAccepted = Array.from(allAcceptedSizes).filter(s => s > itemScu);
        const maxAcceptedSize = Math.max(...Array.from(allAcceptedSizes), 0);

        return {
          commodityId,
          commodityName: item.commodityName || 'Unknown Commodity',
          currentScu: itemScu,
          quantity: Number(item.quantity),
          totalScu: itemScu * Number(item.quantity),
          totalMerchants: buyingMerchants.length,
          acceptedSizes: Array.from(allAcceptedSizes).sort((a, b) => a - b),
          maxAcceptedSize,
          hasLargerOption: largerSizeAccepted.length > 0
        };
      })
      .filter(Boolean);
  }, [cargoItems, merchantCommodities]);

  if (incompatibleItems.length === 0) {
    return null;
  }

  return (
    <div className="scu-incompatibility-warning">
      <div className="warning-header">
        <span className="warning-icon">⚠️</span>
        <div className="warning-title">
          <h4>SCU Box Size Not Accepted</h4>
          <p>
            {incompatibleItems.length} item{incompatibleItems.length > 1 ? 's' : ''} cannot
            be sold at any known location with current box sizes
          </p>
        </div>
      </div>

      <div className="incompatible-items-list">
        {incompatibleItems.map((item, index) => (
          <div key={index} className="incompatible-item">
            <div className="item-info">
              <span className="item-name">{item.commodityName}</span>
              <span className="item-details">
                {item.quantity} × {item.currentScu} SCU boxes = {item.totalScu} SCU total
              </span>
            </div>

            <div className="compatibility-details">
              <div className="no-merchants-warning">
                <span className="cross-icon">✗</span>
                No merchants accept {item.currentScu} SCU boxes for this commodity
              </div>

              {item.acceptedSizes.length > 0 && (
                <div className="accepted-sizes">
                  <span className="accepted-label">
                    Sizes accepted at {item.totalMerchants} merchant{item.totalMerchants > 1 ? 's' : ''}:
                  </span>
                  <div className="size-chips">
                    {item.acceptedSizes.map(size => (
                      <span
                        key={size}
                        className={`size-chip ${size === item.maxAcceptedSize ? 'max-size' : ''}`}
                      >
                        {size} SCU
                        {size === item.maxAcceptedSize && <span className="max-badge">Max</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="action-hint">
                <span className="hint-icon">💡</span>
                <span className="hint-text">
                  {item.currentScu > item.maxAcceptedSize ? (
                    <>
                      Your <strong>{item.currentScu} SCU</strong> boxes are larger than any merchant accepts
                      (max: {item.maxAcceptedSize} SCU). You'll need to find a different buyer or sell a different commodity.
                    </>
                  ) : (
                    <>
                      Try searching for specific locations that accept <strong>{item.currentScu} SCU</strong> boxes,
                      or check if this commodity can be sold elsewhere.
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="warning-footer">
        <span className="footer-tip">
          <strong>Note:</strong> SCU box sizes are set when you purchase cargo.
          Merchants only accept specific box sizes - larger boxes offer better rates but fewer buyers.
        </span>
      </div>
    </div>
  );
};

export default ScuIncompatibilityWarning;
